import useSimpleLocalStorage from "@/hooks/useSimpleLocalStorage";
import {
  mapColumnFiltersStateToFilters,
  shouldRemoveFilterWhenEmpty,
  isReadOnlyFilterType,
} from "@/lib/filters";
import { FilterValue, UseDataTableOptions } from "@/types/dataTableSettings";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

/**
 * Creates table options: filters, sorting, column markers.
 * Filters and sorting are persisted in localStorage. Markers (typing-in-progress) are not.
 */
export function useCreateDataTableOptions<T>(
  name: string,
): UseDataTableOptions<T> {
  /** Column filters – persisted in localStorage */
  const [filters, setFilters] = useSimpleLocalStorage<ColumnFiltersState>(
    `table:filters:${name}`,
    [],
  );

  /** Sorting state – persisted in localStorage */
  const [sorting, setSortedColumns] = useSimpleLocalStorage<SortingState>(
    `table:sorting:${name}`,
    [],
  );

  /**
   * "User is typing" markers – set on every filter input change.
   * Not persisted; used for column highlighting before filter is committed (debounce).
   */
  const [columnFilterMarkers, setColumnFilterMarkers] = useState<
    Record<string, boolean>
  >({});

  /** Updates existing filter. Empty value (except IsEmpty/IsNotEmpty) removes the filter. */
  const updateExistingFilter = useCallback(
    (filters: ColumnFiltersState, index: number, value: FilterValue) => {
      if (shouldRemoveFilterWhenEmpty(value)) {
        filters.splice(index, 1);
      } else {
        filters[index] = { ...filters[index], value };
      }
      return filters;
    },
    [],
  );

  /** Adds a new filter entry to the filters array */
  const createNewFilter = useCallback(
    (filters: ColumnFiltersState, columnId: string, value: FilterValue) => {
      filters.push({ id: columnId, value });
      return filters;
    },
    [],
  );

  /**
   * Sets filter value for a column. Updates existing entry or adds new one.
   * Empty value for normal filter types removes the filter.
   */
  const setFilterValue = useCallback(
    (columnId: string, value: FilterValue) => {
      setFilters((prev) => {
        const newFilters = [...prev];
        const index = newFilters.findIndex((filter) => filter.id === columnId);
        if (index !== -1) {
          updateExistingFilter(newFilters, index, value);
        } else {
          createNewFilter(newFilters, columnId, value);
        }
        return newFilters;
      });
    },
    [setFilters, updateExistingFilter, createNewFilter],
  );

  /** Returns filter value for a column, or undefined if none */
  const getFilterValue = useCallback(
    (columnId: string) => {
      const filter = filters.find((filter) => filter.id === columnId);
      return filter?.value as FilterValue | undefined;
    },
    [filters],
  );

  /** Clears all filters and markers (e.g. "Reset filters" button) */
  const resetFilters = useCallback(() => {
    setFilters([]);
    setColumnFilterMarkers({});
  }, [setFilters, setColumnFilterMarkers]);

  /** Removes filter for a single column and its marker */
  const resetFilterById = useCallback(
    (columnId: string) => {
      setFilters((filters) =>
        filters.filter((filter) => filter.id !== columnId),
      );
      setColumnFilterMarkers((markers) => ({
        ...markers,
        [columnId]: false,
      }));
    },
    [setFilters, setColumnFilterMarkers],
  );

  /** Clears all sorting */
  const resetSorting = useCallback(() => {
    setSortedColumns([]);
  }, [setSortedColumns]);

  /** True when filter is IsEmpty or IsNotEmpty (does not use value) */
  const isNonValueFilter = useCallback(
    (value: FilterValue) => isReadOnlyFilterType(value.filter),
    [],
  );

  /** Whether column has an active marker (typing in filter input, before debounce) */
  const isFilteredInMarkers = useCallback(
    (columnId?: string) => {
      if (columnId) {
        return columnFilterMarkers[columnId];
      }
      return false;
    },
    [columnFilterMarkers],
  );

  /**
   * Whether there is an active filter for the column in persisted filters.
   * Without columnId: whether any filter exists.
   */
  const isFilteredInFilters = useCallback(
    (columnId?: string) => {
      if (columnId) {
        return filters.some((filter) => {
          if (
            isNonValueFilter(filter.value as FilterValue) &&
            filter.id === columnId
          ) {
            return true;
          }
          return (
            filter.id === columnId &&
            (filter.value as FilterValue).value !== undefined
          );
        });
      }
      return filters.length > 0;
    },
    [filters, isNonValueFilter],
  );

  /**
   * Whether column is filtered. Combines markers (immediate reaction while typing)
   * with persisted filters (e.g. after page refresh).
   */
  const isFiltered = useCallback(
    (columnId?: string) => {
      // There are two ways to check if a column is filtered:
      // 1. By checking the markers (bools), which are set by the user as soon as he starts typing in the filter input.
      // 2. By checking the filters (objects), which are set by the user when he finishes typing in the filter input.
      // We want to check the markers first, because they are more likely to be set.
      // But since the markers are not persisted, we need to check the filters if the markers are not set.
      const filteredInMarkers = isFilteredInMarkers(columnId);

      // If the markers are not set, we check the filters.
      if (filteredInMarkers === undefined) {
        return isFilteredInFilters(columnId);
      }

      return filteredInMarkers;
    },
    [isFilteredInMarkers, isFilteredInFilters],
  );

  /** Whether column/sorting is active */
  const isSorted = useCallback(
    (columnId?: string) => {
      if (columnId) {
        return sorting.some((sort) => sort.id === columnId);
      }
      return sorting.length > 0;
    },
    [sorting],
  );

  /** Whether column is filtered or sorted – used for blue highlight on headers/cells */
  const isFilteredOrSorted = useCallback(
    (columnId?: string) => {
      if (columnId) {
        return isFiltered(columnId) || isSorted(columnId);
      }
      return isFiltered() || isSorted();
    },
    [isFiltered, isSorted],
  );

  /** Sort direction for column: "asc" | "desc" | false */
  const getSortValue = useCallback(
    (columnId: string) => {
      const sort = sorting.find((sort) => sort.id === columnId);
      if (sort === undefined) {
        return false;
      }
      return sort.desc ? "desc" : "asc";
    },
    [sorting],
  );

  /** Removes sorting for a single column */
  const clearSorting = useCallback(
    (columnId: string) => {
      setSortedColumns((prev) => prev.filter((sort) => sort.id !== columnId));
    },
    [setSortedColumns],
  );

  /** Sets sorting for a column (or clears when value is false) */
  const setSorting = useCallback(
    (columnId: string, value: false | "asc" | "desc") => {
      setSortedColumns(() => {
        if (value === false) {
          return [];
        }
        return [{ id: columnId, desc: value === "desc" }];
      });
    },
    [setSortedColumns],
  );

  /**
   * Sets column marker – called on every filter input change.
   * true = user typing, false = cleared.
   */
  const setFilteredMark = useCallback(
    (columnId: string, value: boolean) => {
      setColumnFilterMarkers((prev) => {
        // Skip update if value unchanged to avoid unnecessary rerenders
        if (prev[columnId] === value) {
          return prev;
        }
        return {
          ...prev,
          [columnId]: value,
        };
      });
    },
    [setColumnFilterMarkers],
  );

  return useMemo(
    () => ({
      filters: mapColumnFiltersStateToFilters(filters),
      rawFilters: filters,
      setFilters,
      setFilteredMark,
      sorting,
      setSorting,
      setFilterValue,
      getFilterValue,
      resetFilters,
      resetFilterById,
      resetSorting,
      isFilteredOrSorted,
      isFiltered,
      isSorted,
      getSortValue,
      clearSorting,
    }),
    [
      filters,
      setFilters,
      setFilteredMark,
      sorting,
      setSorting,
      setFilterValue,
      getFilterValue,
      resetFilters,
      resetFilterById,
      resetSorting,
      isFilteredOrSorted,
      isFiltered,
      isSorted,
      getSortValue,
      clearSorting,
    ],
  );
}
